import { ReactElement, useContext, useMemo } from 'react';
import { keyBy } from 'lodash-es';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Button,
  CloseButton,
  Dialog,
  DialogOpenChangeDetails,
  IconButton,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';

import { useOpenClose } from '~/core/hooks/useOpenClose';
import { useDocumentFormAction } from '~/core/shared/api/documents';
import { EMPTY_ARR } from '~/core/shared/constants';
import {
  transformBlockValuesOut,
  transformFieldsIntoBlocks,
} from '~/core/shared/fields/FieldUtils';
import { useFormResolver, useRequiredParams } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { useToast } from '~/core/shared/lib/chakraUi/hooks';
import {
  DocumentFormBlock,
  DocumentFormBlockAction,
  DocumentFormBlockActionTriggerButton,
} from '~/core/shared/types';
import { isDeniedHook } from '~/core/shared/utils/general';
import { ApplicationContext, DocumentFormContext } from '../application';
import { TenantIcon } from '../TenantIcon.component';
import { OptionalFieldVisibility } from './OptionalFieldVisibility/OptionalFieldVisibility';
import {
  FormFieldRenderer,
  supportedBlockRenderTypes,
} from './FormFieldRenderer.component';
import { generateSchema } from './GenerateSchema.component';
import { ErrorDisplay } from './MainPanel.component';
import { UnsavedModal } from './UnsavedModal.component';
import { useBackendErrors } from './useBackendErrors.hook';

export type Sizes = 'sm' | 'md' | 'lg';

type HandlerFunctionEventType = { stopPropagation: () => void };

type ActionHandlerType = {
  path: string;
  action: DocumentFormBlockAction;
  block: DocumentFormBlock;
  fullButton?: boolean;
  size?: Sizes;
};

type ActionHandlerButtonProps = {
  fullButton: boolean;
  action: DocumentFormBlockActionTriggerButton & DocumentFormBlockAction;
  onClick: (event: HandlerFunctionEventType) => void;
  size?: Sizes;
  disabled?: boolean;
};

const ActionHandlerButton = ({
  fullButton = true,
  action,
  onClick,
  size = 'sm',
  disabled = false,
}: ActionHandlerButtonProps): ReactElement =>
  fullButton ? (
    <Button key={action.key} disabled={disabled} onClick={onClick} size={size}>
      <TenantIcon name={action.triggerOptions.icon} />
      {action.triggerOptions.label}
    </Button>
  ) : (
    <IconButton
      aria-label={action.triggerOptions.label}
      border="none"
      disabled={disabled}
      onClick={onClick}
      size="md"
    >
      <TenantIcon name={action.triggerOptions.icon} />
    </IconButton>
  );

const ActionHandler = ({
  path,
  action,
  fullButton,
  size = 'sm',
}: ActionHandlerType): ReactElement => {
  // @TODO: 'doc' is not correct here, should be document definition we're currently viewing
  const { postFormAction, isPending } = useDocumentFormAction({
    definition: 'doc',
  });
  const {
    formIdentifier,
    updateFormBlocksById,
    refreshFormContent,
    setSelectedBlock,
    refetchForm,
  } = useContext(DocumentFormContext);
  const toast = useToast();
  const { uuid } = useRequiredParams(['uuid']);
  const actionModalApi = useOpenClose();
  const unsavedChangesModalApi = useOpenClose();
  const { hooks } = useContext(ApplicationContext);
  const disabled = isDeniedHook(hooks, 'documentFormUpdate');

  const handleOpenModal = (event: HandlerFunctionEventType): void => {
    event.stopPropagation();
    actionModalApi.onOpen();
  };

  const formFields =
    action.handler === 'modal' ? action.handlerOptions.fields : EMPTY_ARR;

  // TODO: @haxxxton refactor to use useFormBlocks
  const resolver = useFormResolver(generateSchema(formFields));
  const form = useForm({
    mode: 'onChange',
    resolver,
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = form;

  const { getError, clearBackendErrors, setBackendErrorsFromResponse } =
    useBackendErrors(errors);

  const blocks = useMemo(
    () =>
      transformFieldsIntoBlocks(
        formFields.filter((field) =>
          supportedBlockRenderTypes.includes(field.type),
        ),
      ),
    [formFields],
  );

  const handleCloseAndReset = useEventHandler((): void => {
    clearBackendErrors();
    reset();
    actionModalApi.onClose();
    unsavedChangesModalApi.onClose();
  });

  const onSubmit = useEventHandler(async (formData: any): Promise<void> => {
    clearBackendErrors();

    const values = transformBlockValuesOut(formData, blocks);

    try {
      const response = await postFormAction({
        documentId: uuid,
        data: values,
        formIdentifier,
        action: action.key,
        path,
      });

      const responseFormMeta = response?.data?.meta;
      const responseBlocks = keyBy(
        response?.data?.content ?? [],
        (contentBlock) => contentBlock.meta.id,
      );

      if (action.key.includes('create')) {
        refetchForm();
        if (response?.data?.content?.[0])
          setSelectedBlock(response.data.content[0]);
      } else if (responseFormMeta?.scoped) {
        updateFormBlocksById(responseBlocks);
      } else {
        refreshFormContent(responseBlocks);
      }

      toast({
        closable: true,
        type: 'success',
        title:
          action.handler === 'modal' && action.handlerOptions?.successMessage
            ? action.handlerOptions.successMessage
            : 'Action successful',
      });

      handleCloseAndReset();
    } catch (error: any) {
      if (error?.response?.status === 422) {
        setBackendErrorsFromResponse(error?.response?.data);
      } else {
        toast({
          closable: true,
          type: 'error',
          title:
            action.handler === 'modal' && action.handlerOptions?.errorMessage
              ? action.handlerOptions.errorMessage
              : (error?.message ?? 'Action failed'),
        });
      }
    }
  });

  const handleClose = (): void => {
    if (isDirty) {
      unsavedChangesModalApi.onOpen();
    } else {
      clearBackendErrors();
      reset();
      actionModalApi.onClose();
    }
  };

  // TODO: move to onClose prop of useOpenClose
  const onOpenChange = ({ open }: DialogOpenChangeDetails): void => {
    if (open) return;
    handleClose();
  };

  const hasFormFields = formFields.length > 0;

  return (
    <>
      {action.trigger === 'button' && (
        <ActionHandlerButton
          action={action}
          disabled={disabled}
          fullButton={fullButton || action.triggerOptions.fullButton}
          onClick={(e) => handleOpenModal(e)}
          size={size}
        />
      )}
      {action.handler === 'modal' && actionModalApi.open && (
        <>
          <Dialog.Root
            onOpenChange={onOpenChange}
            open={actionModalApi.open}
            placement="center"
            preventScroll
            scrollBehavior="inside"
          >
            <Portal>
              <Dialog.Backdrop />
              <FormProvider {...form}>
                <Dialog.Positioner>
                  <Dialog.Content as="form" onSubmit={handleSubmit(onSubmit)}>
                    <Dialog.Header>
                      <Dialog.Title>{action.handlerOptions.title}</Dialog.Title>
                    </Dialog.Header>
                    <Dialog.CloseTrigger asChild>
                      <CloseButton />
                    </Dialog.CloseTrigger>

                    <Dialog.Body>
                      <Stack gap={3}>
                        {hasFormFields &&
                          formFields.map((field, index) => (
                            <OptionalFieldVisibility
                              // eslint-disable-next-line react/no-array-index-key
                              key={index}
                              field={field}
                            >
                              <Stack gap={0.5}>
                                <Text>
                                  {field.label}
                                  <Text as="span" color="red.500">
                                    {field.required ? '*' : null}
                                  </Text>
                                </Text>
                                <FormFieldRenderer field={field} />
                                <ErrorDisplay error={getError(field.name)} />
                              </Stack>
                            </OptionalFieldVisibility>
                          ))}
                        {!hasFormFields && (
                          <Text>
                            {action.handlerOptions.bodyText || 'Are you sure?'}
                          </Text>
                        )}
                      </Stack>
                    </Dialog.Body>

                    <Dialog.Footer>
                      <Button disabled={isPending} mr={3} onClick={handleClose}>
                        Close
                      </Button>
                      <Button
                        colorPalette={
                          action.handlerOptions.buttonColor || 'primary'
                        }
                        disabled={isPending || (!isDirty && hasFormFields)}
                        loading={isPending}
                        onClick={handleSubmit(onSubmit)}
                        type="submit"
                        variant="solid"
                      >
                        Submit
                      </Button>
                    </Dialog.Footer>
                  </Dialog.Content>
                </Dialog.Positioner>
              </FormProvider>
            </Portal>
          </Dialog.Root>
          <UnsavedModal
            close={handleCloseAndReset}
            modalApi={unsavedChangesModalApi}
          />
        </>
      )}
    </>
  );
};

export { ActionHandler };
